import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DesktopSyncMode, LocalCashierPublicProfile, UnlockResult } from './types';
import type { User } from '@/types';

interface DesktopSessionState {
  /** True when a cashier unlocked via local PIN/fingerprint (may be offline). */
  locallyUnlocked: boolean;
  locked: boolean;
  cloudSessionExpired: boolean;
  activeProfile: LocalCashierPublicProfile | null;
  pendingEnrollAfterLogin: boolean;
  /** True when cloud login should open PIN enroll (forgot-PIN / first enroll). */
  resetPinAfterLogin: boolean;
  setupComplete: boolean;
  boundCompanyCode: string | null;
  desktopSyncMode: DesktopSyncMode | null;
  pendingDesktopSetup: boolean;
  setLocked: (v: boolean) => void;
  setCloudSessionExpired: (v: boolean) => void;
  setPendingEnrollAfterLogin: (v: boolean) => void;
  setResetPinAfterLogin: (v: boolean) => void;
  setSetupState: (patch: {
    setupComplete?: boolean;
    boundCompanyCode?: string | null;
    desktopSyncMode?: DesktopSyncMode | null;
    pendingDesktopSetup?: boolean;
  }) => void;
  applyUnlock: (result: UnlockResult) => {
    user: User;
    company: { code: string; name: string; status: string };
    role: string;
    canUsePOS: boolean;
  } | null;
  clearLocalUnlock: () => void;
  /** Lock screen but keep auth profiles / persisted cloud snapshot. */
  lockScreen: () => void;
}

export const useDesktopSessionStore = create<DesktopSessionState>()(
  persist(
    (set) => ({
      locallyUnlocked: false,
      locked: false,
      cloudSessionExpired: false,
      activeProfile: null,
      pendingEnrollAfterLogin: false,
      resetPinAfterLogin: false,
      setupComplete: false,
      boundCompanyCode: null,
      desktopSyncMode: null,
      pendingDesktopSetup: false,
      setLocked: (v) => set({ locked: v }),
      setCloudSessionExpired: (v) => set({ cloudSessionExpired: v }),
      setPendingEnrollAfterLogin: (v) => set({ pendingEnrollAfterLogin: v }),
      setResetPinAfterLogin: (v) => set({ resetPinAfterLogin: v }),
      setSetupState: (patch) => set(patch),
      applyUnlock: (result) => {
        const snap = result.userSnapshot;
        set({
          locallyUnlocked: true,
          locked: false,
          activeProfile: result.profile,
          // Trust vault restore when applied; verify may flip to true on failure.
          cloudSessionExpired: !result.cloudApplied,
        });
        if (!snap?.user || !result.profile.companyCode) return null;
        const company = snap.company || {
          code: result.profile.companyCode,
          name: result.profile.companyName || result.profile.companyCode,
          status: 'Active',
        };
        return {
          user: snap.user as User,
          company,
          role: snap.role || result.profile.role,
          canUsePOS: snap.can_use_pos ?? result.profile.canUsePOS,
        };
      },
      clearLocalUnlock: () =>
        set({
          locallyUnlocked: false,
          locked: false,
          activeProfile: null,
          cloudSessionExpired: false,
        }),
      lockScreen: () => set({ locked: true, locallyUnlocked: false }),
    }),
    {
      name: 'desktop-session',
      partialize: (s) => ({
        activeProfile: s.activeProfile,
        // Never persist unlocked=true across restart — always require unlock.
        pendingEnrollAfterLogin: s.pendingEnrollAfterLogin,
        resetPinAfterLogin: s.resetPinAfterLogin,
        setupComplete: s.setupComplete,
        boundCompanyCode: s.boundCompanyCode,
        desktopSyncMode: s.desktopSyncMode,
        pendingDesktopSetup: s.pendingDesktopSetup,
      }),
    },
  ),
);
